import { useState, useEffect } from 'react';
import {
  FileCheck,
  Upload,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  FileText,
  Image,
  File,
  Download,
  Trash2,
  Search,
  X,
} from 'lucide-react';
import type { ComplianceEvidence, EvidenceStatus, ComplianceControl } from '../types/compliance';
import './Evidence.css';

export default function Evidence() {
  const [evidence, setEvidence] = useState<ComplianceEvidence[]>([]);
  const [controls, setControls] = useState<ComplianceControl[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<EvidenceStatus | 'all'>('all');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [newEvidence, setNewEvidence] = useState({
    name: '',
    description: '',
    controlId: '',
    type: 'document' as ComplianceEvidence['type'],
    expiresAt: '',
    fileUrl: '',
  });

  const fetchEvidence = async () => {
    try {
      const res = await fetch('/api/compliance/evidence');
      if (res.ok) {
        const data = await res.json();
        setEvidence(data);
      } else {
        setError('Failed to load evidence');
      }
    } catch (err) {
      console.error('Failed to fetch evidence:', err);
      setError('Failed to load evidence');
    }
  };

  const fetchControls = async () => {
    try {
      const res = await fetch('/api/compliance/controls');
      if (res.ok) {
        const data = await res.json();
        setControls(data);
      }
    } catch (err) {
      console.error('Failed to fetch controls:', err);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      await Promise.all([fetchEvidence(), fetchControls()]);
      setLoading(false);
    };
    loadData();
  }, []);

  const handleCreateEvidence = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch('/api/compliance/evidence', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newEvidence,
          expiresAt: newEvidence.expiresAt || undefined,
          fileUrl: newEvidence.fileUrl || undefined,
        }),
      });
      if (res.ok) {
        setShowUploadModal(false);
        setNewEvidence({
          name: '',
          description: '',
          controlId: '',
          type: 'document',
          expiresAt: '',
          fileUrl: '',
        });
        fetchEvidence();
      }
    } catch (err) {
      console.error('Failed to create evidence:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteEvidence = async (evidenceId: string) => {
    if (!confirm('Are you sure you want to delete this evidence?')) return;
    try {
      const res = await fetch(`/api/compliance/evidence/${evidenceId}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        fetchEvidence();
      }
    } catch (err) {
      console.error('Failed to delete evidence:', err);
    }
  };

  const filteredEvidence = evidence.filter((item) => {
    const matchesSearch =
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.controlName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = selectedStatus === 'all' || item.status === selectedStatus;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadgeClass = (status: EvidenceStatus) => {
    switch (status) {
      case 'approved': return 'badge-passing';
      case 'collected': return 'badge-info';
      case 'pending': return 'badge-warning';
      case 'expired': return 'badge-failing';
      case 'rejected': return 'badge-critical';
      default: return 'badge-info';
    }
  };

  const getTypeIcon = (type: ComplianceEvidence['type']) => {
    switch (type) {
      case 'document': return <FileText size={18} />;
      case 'screenshot': return <Image size={18} />;
      case 'policy': return <FileCheck size={18} />;
      default: return <File size={18} />;
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getDaysUntilExpiry = (expiresAt?: string) => {
    if (!expiresAt) return null;
    const diff = new Date(expiresAt).getTime() - Date.now();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const stats = {
    total: evidence.length,
    approved: evidence.filter((e) => e.status === 'approved').length,
    pending: evidence.filter((e) => e.status === 'pending').length,
    expiringSoon: evidence.filter((e) => {
      const days = getDaysUntilExpiry(e.expiresAt);
      return days !== null && days > 0 && days <= 30;
    }).length,
    expired: evidence.filter((e) => e.status === 'expired').length,
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Evidence Collection</h1>
        </div>
        <div style={{ textAlign: 'center', padding: '2rem', color: '#a6adc8' }}>
          Loading evidence...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Evidence Collection</h1>
        </div>
        <div style={{ textAlign: 'center', padding: '2rem', color: '#f38ba8' }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <h1>Evidence Collection</h1>
          <p>Upload and manage compliance evidence for controls</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowUploadModal(true)}>
          <Upload size={18} />
          Upload Evidence
        </button>
      </div>

      {/* Stats */}
      <div className="evidence-stats">
        <div className="evidence-stat">
          <FileCheck size={20} />
          <div className="evidence-stat-content">
            <span className="evidence-stat-value">{stats.approved}</span>
            <span className="evidence-stat-label">Approved</span>
          </div>
        </div>
        <div className="evidence-stat warning">
          <Clock size={20} />
          <div className="evidence-stat-content">
            <span className="evidence-stat-value">{stats.pending}</span>
            <span className="evidence-stat-label">Pending</span>
          </div>
        </div>
        <div className="evidence-stat caution">
          <AlertTriangle size={20} />
          <div className="evidence-stat-content">
            <span className="evidence-stat-value">{stats.expiringSoon}</span>
            <span className="evidence-stat-label">Expiring Soon</span>
          </div>
        </div>
        <div className="evidence-stat danger">
          <XCircle size={20} />
          <div className="evidence-stat-content">
            <span className="evidence-stat-value">{stats.expired}</span>
            <span className="evidence-stat-label">Expired</span>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="evidence-toolbar">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search evidence..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value as EvidenceStatus | 'all')}
          className="filter-select"
        >
          <option value="all">All Statuses</option>
          <option value="approved">Approved</option>
          <option value="collected">Collected</option>
          <option value="pending">Pending</option>
          <option value="expired">Expired</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {/* Evidence Grid */}
      <div className="evidence-grid">
        {filteredEvidence.map((item) => {
          const daysUntilExpiry = getDaysUntilExpiry(item.expiresAt);
          const isExpiringSoon = daysUntilExpiry !== null && daysUntilExpiry > 0 && daysUntilExpiry <= 30;

          return (
            <div key={item.id} className="evidence-card">
              <div className="evidence-card-header">
                <div className="evidence-type-icon">{getTypeIcon(item.type)}</div>
                <span className={`badge ${getStatusBadgeClass(item.status)}`}>
                  {item.status}
                </span>
              </div>

              <h3 className="evidence-name">{item.name}</h3>
              <p className="evidence-description">{item.description}</p>

              <div className="evidence-control">
                <span className="evidence-control-id">{item.controlId}</span>
                <span className="evidence-control-name">{item.controlName}</span>
              </div>

              <div className="evidence-meta">
                {item.collectedAt && (
                  <div className="evidence-meta-item">
                    <CheckCircle size={14} />
                    <span>Collected: {formatDate(item.collectedAt)}</span>
                  </div>
                )}
                {item.expiresAt && (
                  <div className={`evidence-meta-item ${isExpiringSoon ? 'warning' : ''}`}>
                    <Clock size={14} />
                    <span>
                      {daysUntilExpiry && daysUntilExpiry > 0
                        ? `Expires in ${daysUntilExpiry} days`
                        : 'Expired'}
                    </span>
                  </div>
                )}
              </div>

              <div className="evidence-actions">
                {item.fileUrl && (
                  <a href={item.fileUrl} target="_blank" rel="noopener noreferrer" className="evidence-action-btn">
                    <Download size={16} />
                  </a>
                )}
                <button
                  className="evidence-action-btn danger"
                  onClick={() => handleDeleteEvidence(item.id)}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {filteredEvidence.length === 0 && (
        <div className="empty-state">
          <FileCheck size={48} />
          <h3>No evidence found</h3>
          <p>Upload evidence to get started</p>
        </div>
      )}

      {/* Upload Evidence Modal */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Upload Evidence</h2>
              <button className="close-btn" onClick={() => setShowUploadModal(false)}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCreateEvidence}>
              <div className="form-group">
                <label>Evidence Name *</label>
                <input
                  type="text"
                  value={newEvidence.name}
                  onChange={(e) => setNewEvidence({ ...newEvidence, name: e.target.value })}
                  placeholder="e.g., Annual Security Training Certificate"
                  required
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={newEvidence.description}
                  onChange={(e) => setNewEvidence({ ...newEvidence, description: e.target.value })}
                  placeholder="Describe this evidence..."
                  rows={3}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Related Control *</label>
                  <select
                    value={newEvidence.controlId}
                    onChange={(e) => setNewEvidence({ ...newEvidence, controlId: e.target.value })}
                    required
                  >
                    <option value="">Select a control...</option>
                    {controls.map((control) => (
                      <option key={control.id} value={control.id}>
                        {control.id} - {control.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Evidence Type</label>
                  <select
                    value={newEvidence.type}
                    onChange={(e) => setNewEvidence({ ...newEvidence, type: e.target.value as ComplianceEvidence['type'] })}
                  >
                    <option value="document">Document</option>
                    <option value="screenshot">Screenshot</option>
                    <option value="log">Log</option>
                    <option value="audit">Audit Report</option>
                    <option value="policy">Policy</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Expiration Date</label>
                  <input
                    type="date"
                    value={newEvidence.expiresAt}
                    onChange={(e) => setNewEvidence({ ...newEvidence, expiresAt: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>File URL</label>
                  <input
                    type="url"
                    value={newEvidence.fileUrl}
                    onChange={(e) => setNewEvidence({ ...newEvidence, fileUrl: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowUploadModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Uploading...' : 'Upload Evidence'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
